import { getUser } from "@/auth/oidc";

/**
 * Minimal typed fetch wrapper that injects the Keycloak access token on every
 * request. The generated TS client (from `npm run gen:api`) wraps this.
 *
 * On 401, callers are responsible for triggering a silent renew via the auth
 * provider, then retrying. We don't auto-retry here to keep behaviour
 * predictable for TanStack Query.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    super(`API ${status} ${statusText}`);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const user = await getUser();

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (user?.access_token) {
    headers.set("Authorization", `Bearer ${user.access_token}`);
  }

  const response = await fetch(path, { ...init, headers });

  if (!response.ok) {
    const body = await response.text();
    let parsed: unknown = body;
    try {
      parsed = JSON.parse(body);
    } catch {
      // body is plain text, keep as-is
    }
    throw new ApiError(response.status, response.statusText, parsed);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
